/**
 * Storage key holding the portal's selected organisation id.
 *
 * PortalDataProvider writes it, portalApi sends it as
 * X-Platform-Organisation-Id, and the executions page keys its count memo by
 * it. One constant so a rename moves all of them (and the Playwright specs
 * that seed it) together. Kept dependency-free so Playwright can import it.
 */
export const SELECTED_ORGANISATION_STORAGE_KEY =
  "portal:selectedOrganisationId";

// The selection is per TAB. localStorage is shared by every tab, and the
// gateway forwards X-Platform-Organisation-Id as the caller's org, so a
// selection kept only there made a tab left on org A send whichever org was
// last picked in any tab, while its own URLs and screens still said A.
//
// Where this tab's selection lives, read in this order:
//  1. memory: this tab's current choice. Always set, even when storage
//     refuses a write, so the header never lags what the picker shows.
//  2. sessionStorage: the same choice, kept across a reload of this tab.
//  3. localStorage: the org last chosen in the org picker of any tab, used
//     only as the starting org of a tab that has none yet (a new tab, or the
//     Playwright specs, which seed localStorage). The first read adopts it as
//     this tab's own, so a later switch in another tab cannot move this tab.

let tabSelection: string | null = null;

function read(storage: () => Storage): string | null {
  try {
    return storage().getItem(SELECTED_ORGANISATION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, organisationId: string): boolean {
  try {
    storage().setItem(SELECTED_ORGANISATION_STORAGE_KEY, organisationId);
    return true;
  } catch {
    return false;
  }
}

function remove(storage: () => Storage): void {
  try {
    storage().removeItem(SELECTED_ORGANISATION_STORAGE_KEY);
  } catch {
    // Storage blocked: nothing stored to remove.
  }
}

function rememberForThisTab(organisationId: string): void {
  tabSelection = organisationId;
  // A refused write must not leave an older choice behind for a reload to
  // pick up instead of this one.
  if (!write(() => window.sessionStorage, organisationId)) {
    remove(() => window.sessionStorage);
  }
}

/** This tab's selected organisation (adopting the shared default if none). */
export function readSelectedOrganisationId(): string | null {
  if (tabSelection) return tabSelection;
  if (typeof window === "undefined") return null;
  const own = read(() => window.sessionStorage);
  if (own) {
    tabSelection = own;
    return own;
  }
  const shared = read(() => window.localStorage);
  if (shared) rememberForThisTab(shared);
  return shared;
}

/**
 * Settle this tab's selection without changing the default for new tabs:
 * PortalDataProvider re-runs this on every org-list load, including the one a
 * token renewed in another tab triggers.
 */
export function keepSelectedOrganisationIdForThisTab(
  organisationId: string,
): void {
  if (typeof window === "undefined") return;
  rememberForThisTab(organisationId);
}

/** The user picked an org: this tab's selection, and the default for new tabs. */
export function writeSelectedOrganisationId(organisationId: string): void {
  if (typeof window === "undefined") return;
  rememberForThisTab(organisationId);
  write(() => window.localStorage, organisationId);
}

/**
 * The org list for this tab's user is in: if the default for new tabs is
 * missing (cleared by a sign-out) or names an org they cannot use (membership
 * removed), set it to the one this tab settled on.
 *
 * Missing matters as much as wrong: a new tab, and any tab still running the
 * release before per-tab selection (which reads only this default), sends no
 * X-Platform-Organisation-Id while it is empty, and the gateway refuses a
 * member's request that names no org.
 */
export function replaceSelectedOrganisationDefaultIfUnusable(
  organisationIds: readonly string[],
  organisationId: string,
): void {
  if (typeof window === "undefined") return;
  const shared = read(() => window.localStorage);
  if (!shared || !organisationIds.includes(shared)) {
    write(() => window.localStorage, organisationId);
  }
}

/**
 * Forget the selection: this tab's and the default for new tabs. Called when
 * this tab signs out (which also removes the session every tab shares) or
 * signs in as someone else, so the next person starts from their own
 * organisations, not the previous user's choice.
 */
export function forgetSelectedOrganisationId(): void {
  tabSelection = null;
  if (typeof window === "undefined") return;
  remove(() => window.sessionStorage);
  remove(() => window.localStorage);
}
